import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Spinner,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  useId,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  DocumentRegular,
  ImageRegular,
  TextBulletListLtr24Regular,
} from "@fluentui/react-icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useExportPdf } from "../hooks/useExportPdf";
import type {
  Call,
  CallContentDto,
  InstructionSet,
  TranscriptSession,
} from "../types";

interface ExportPdfMenuProps {
  callId: number;
  call: Call;
  content: CallContentDto | undefined;
  instructionSet: InstructionSet | undefined;
  transcriptSession: TranscriptSession | undefined;
  domainId: number;
  hasTranscript: boolean;
  hasImages: boolean;
}

export default function ExportPdfMenu({
  callId,
  call,
  content,
  instructionSet,
  transcriptSession,
  domainId,
  hasTranscript,
  hasImages,
}: ExportPdfMenuProps) {
  const { t } = useTranslation();
  const toasterId = useId("export-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const { exportCallReport, exportTranscript, exportImages, isExporting, exportError, clearExportError } = useExportPdf({
    callId,
    call,
    content,
    instructionSet,
    transcriptSession,
    domainId,
  });

  useEffect(() => {
    if (exportError) {
      dispatchToast(
        <Toast>
          <ToastTitle>{t('error')}</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
      clearExportError();
    }
  }, [exportError, dispatchToast, clearExportError, t]);

  return (
    <>
      <Toaster toasterId={toasterId} />
      <Menu>
        <MenuTrigger>
          <Button
            appearance="subtle"
            icon={
              isExporting ? (
                <Spinner size="tiny" />
              ) : (
                <ArrowDownloadRegular />
              )
            }
            disabled={isExporting}
          >
            {t("export")}
          </Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              icon={<DocumentRegular />}
              onClick={exportCallReport}
              disabled={isExporting}
            >
              {t("callReport")}
            </MenuItem>
            {hasTranscript && (
              <MenuItem
                icon={<TextBulletListLtr24Regular />}
                onClick={exportTranscript}
                disabled={isExporting}
              >
                {t("fullTranscript")}
              </MenuItem>
            )}
            {hasImages && (
              <MenuItem
                icon={<ImageRegular />}
                onClick={exportImages}
                disabled={isExporting}
              >
                {t("exportImages")}
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
    </>
  );
}
